import { FunctionComponent, useContext, useCallback, useMemo } from "react";

import { BetslipCastBet, PebbleList } from "@ppb/the-wall-web";
import ConnectedCastRunner from "../CastRunner";
import { CastRunner } from "../CastRunner/CastRunner.web";
import ConnectedBetControls from "../BetControls";
import { BetControls } from "../BetControls/BetControls.web";

import { ComponentProps } from "./props";
import { KeyboardContext } from "../Keyboard/KeyboardContext";
import { RootBetslipContext } from "../RootBetslip/RootBetslipContext";

export const CastBet: FunctionComponent<ComponentProps> = ({
  config,
  id,
  title,
  selectedCastType,
  runnersOrder,
  castTypes,
  isOrderable,
  shouldFocusStakeField,
  dispatchCastBetChange,
  dispatchOrderChange,
  dispatchConfirmCastBetChange,
}) => {
  const { setFocusedKeyboardControls } = useContext(KeyboardContext);
  const { isDesktopLayout } = config;
  const { isBetConfirmationStep } = useContext(RootBetslipContext);

  const handleCastBetChange = useCallback(
    (pebbleId: string) => {
      // Dismiss keyboard when selected cast type changes
      if (id !== pebbleId) {
        setFocusedKeyboardControls((prev) => ({
          ...prev,
          focusedInputId: null,
        }));
      }

      if (isBetConfirmationStep) {
        dispatchConfirmCastBetChange(id, pebbleId);
      } else {
        dispatchCastBetChange(id, pebbleId);
      }
    },
    [id, isBetConfirmationStep, setFocusedKeyboardControls, dispatchConfirmCastBetChange, dispatchCastBetChange],
  );
  const handleOrderChange = useCallback(
    (changedRunnerId: string, order: string[]) => {
      if (selectedCastType) {
        dispatchOrderChange(selectedCastType, changedRunnerId, order);
      }
    },
    [dispatchOrderChange, selectedCastType],
  );

  const pebbleList = useMemo(
    () =>
      selectedCastType && castTypes?.length ? (
        <PebbleList
          items={castTypes}
          defaultSelectedPebble={selectedCastType}
          onPebbleClick={handleCastBetChange}
          isDesktopLayout={isDesktopLayout}
        />
      ) : undefined,
    [castTypes, selectedCastType, isDesktopLayout, handleCastBetChange],
  );
  const runners = useMemo(
    () =>
      runnersOrder.map((runnerId) => (
        <ConnectedCastRunner key={runnerId} component={CastRunner} id={runnerId} isOrderable={isOrderable} />
      )),
    [isOrderable, runnersOrder],
  );
  const controls = useMemo(
    () => (
      <ConnectedBetControls
        component={BetControls}
        combinationId={selectedCastType || ""}
        isDividend
        shouldFocusStakeField={shouldFocusStakeField}
        hasAvailabilityHints
      />
    ),
    [selectedCastType, shouldFocusStakeField],
  );

  if (!id || !title || !selectedCastType || !castTypes) {
    return null;
  }

  return (
    <BetslipCastBet
      // Remount when the runners change: DraggableList positions each newly added row at
      // `order.indexOf(index)` against its previous order, which is -1 for a new row,
      // so the row renders one slot above the list, on top of the cast type pebbles.
      // Re-keying makes DraggableList rebuild its order from the current runners.
      key={runnersOrder.join("|")}
      title={title}
      runnersOrder={runnersOrder}
      runners={runners}
      controls={controls}
      castTypes={pebbleList}
      onRunnerOrderChange={isOrderable ? handleOrderChange : undefined}
      isDesktop={isDesktopLayout}
    />
  );
};
